import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { Container, FormControl, FormLabel } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import FormGroup from "react-bootstrap/FormGroup";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation } from "react-router-dom";
import {
  NEW_PASSWORD,
  NEW_PASSWORD_CONFIRM,
  PASSWORD_RESETTED_SUCCESSFULLY,
  TOKEN_INVALID
} from "../../../ducks/forgotPassword";
import { MAX_PASSWORD_CHARS, MIN_PASSWORD_CHARS } from "../../../ducks/userManagement";
import StandardView from "../../common/StandardView";

const PasswordTokenReset = ({
  newPassword,
  newPasswordConfirm,
  newPasswordIsValid,
  newPasswordConfirmIsValid,
  propertyChange,
  validateToken,
  resetPassword,
  tokenInvalid,
  passwordResettedSuccessfully,
  clearMessages
}) => {
  const location = useLocation();
  const intl = useIntl();
  const query = new URLSearchParams(location.search);
  const tokenSubmitted = query && query.get("token");

  useEffect(() => {
    clearMessages();
    if (tokenSubmitted) {
      validateToken(tokenSubmitted);
    }

    return () => {
      clearMessages();

      propertyChange(TOKEN_INVALID, false);
      propertyChange(PASSWORD_RESETTED_SUCCESSFULLY, false);
      propertyChange(NEW_PASSWORD, "");
      propertyChange(NEW_PASSWORD_CONFIRM, "");
    };
  }, [clearMessages, tokenSubmitted, propertyChange, validateToken]);

  return (
    <StandardView id="forgotPassword" titleKey="login.reset.password.page.title">
      <Button as={Link} size="sm" variant="link" to="/login">
        <FontAwesomeIcon className="me-2" icon={faChevronLeft} />
        <FormattedMessage id="login.back.to.login.page" />
      </Button>
      {tokenSubmitted && !passwordResettedSuccessfully && !tokenInvalid && (
        <Container>
          <h5>
            <FormattedMessage id="login.please.specify.a.new.password" />
          </h5>
          <FormGroup className="mb-3">
            <FormLabel>
              <FormattedMessage id="user.profile.form.new.password" />
            </FormLabel>
            <FormControl
              type="password"
              name="newPassword"
              defaultValue={newPassword || ""}
              isValid={newPassword && newPasswordIsValid}
              onChange={(event) => propertyChange("newPassword", event.target.value)}
            />
            {!newPasswordIsValid && (
              <p className="text-danger">
                {intl.formatMessage(
                  { id: "user.profile.form.new.password.invalid.message" },
                  {
                    MIN_PASSWORD_CHARS,
                    MAX_PASSWORD_CHARS
                  }
                )}
              </p>
            )}
          </FormGroup>
          <FormGroup className="mb-3">
            <FormLabel>
              <FormattedMessage id="user.profile.form.new.password.confirm" />
            </FormLabel>
            <FormControl
              type="password"
              name="newPasswordConfirm"
              defaultValue={newPasswordConfirm || ""}
              isValid={newPasswordConfirm && newPasswordConfirmIsValid}
              onChange={(event) => propertyChange("newPasswordConfirm", event.target.value)}
            />
            {!newPasswordConfirmIsValid && (
              <p className="text-danger">
                <FormattedMessage id="user.profile.form.new.password.confirm.invalid.message" />
              </p>
            )}
          </FormGroup>
          <Button
            onClick={resetPassword}
            disabled={!newPassword || !newPasswordConfirm || !newPasswordIsValid || !newPasswordConfirmIsValid}
          >
            <FormattedMessage id="master.data.management.button.save" />
          </Button>
        </Container>
      )}
    </StandardView>
  );
};

PasswordTokenReset.propTypes = {
  newPassword: PropTypes.string,
  newPasswordConfirm: PropTypes.string,
  newPasswordIsValid: PropTypes.bool,
  newPasswordConfirmIsValid: PropTypes.bool,
  tokenInvalid: PropTypes.bool,
  passwordResettedSuccessfully: PropTypes.bool,

  propertyChange: PropTypes.func.isRequired,
  requestPasswordReset: PropTypes.func.isRequired,
  resetPassword: PropTypes.func.isRequired,
  validateToken: PropTypes.func.isRequired,
  clearMessages: PropTypes.func.isRequired,

  intl: PropTypes.object
};

export default PasswordTokenReset;
